import type { Persona } from '../ai/personas';
import { showToast } from './Toast';

export interface LetterModalState {
  persona: Persona;
}

export interface LetterCallbacks {
  onSend: (text: string) => Promise<void>;
  onClose: () => void;
  onPostcard?: () => void;
}

export class LetterModal {
  private root: HTMLDivElement;
  private titleEl: HTMLDivElement;
  private subEl: HTMLDivElement;
  private replyEl: HTMLDivElement;
  private textareaEl: HTMLTextAreaElement;
  private sendBtn: HTMLButtonElement;
  private closeBtn: HTMLButtonElement;
  private postcardBtn: HTMLButtonElement;
  private toastEl: HTMLDivElement;
  private currentPersona: Persona | null = null;
  private cb: LetterCallbacks | null = null;
  private busy = false;

  constructor(host: HTMLElement) {
    this.root = document.createElement('div');
    this.root.className = 'layer letter ignore-aim hidden';
    this.root.innerHTML = `
      <div class="letter-card">
        <div class="letter-head">
          <div class="letter-title">老先生</div>
          <button class="letter-close" aria-label="关闭">×</button>
        </div>
        <div class="letter-sub">云海座头鲸</div>
        <div class="letter-reply"></div>
        <textarea class="letter-input" rows="3" placeholder="写一句话给 TA..." maxlength="140"></textarea>
        <div class="letter-actions">
          <button class="letter-postcard" disabled>生成明信片</button>
          <button class="letter-send">发送</button>
        </div>
        <div class="letter-toast"></div>
      </div>
    `;
    host.appendChild(this.root);
    this.titleEl = this.root.querySelector('.letter-title') as HTMLDivElement;
    this.subEl = this.root.querySelector('.letter-sub') as HTMLDivElement;
    this.replyEl = this.root.querySelector('.letter-reply') as HTMLDivElement;
    this.textareaEl = this.root.querySelector('.letter-input') as HTMLTextAreaElement;
    this.sendBtn = this.root.querySelector('.letter-send') as HTMLButtonElement;
    this.closeBtn = this.root.querySelector('.letter-close') as HTMLButtonElement;
    this.postcardBtn = this.root.querySelector('.letter-postcard') as HTMLButtonElement;
    this.toastEl = this.root.querySelector('.letter-toast') as HTMLDivElement;

    this.sendBtn.addEventListener('click', () => this.handleSend());
    this.closeBtn.addEventListener('click', () => this.handleClose());
    this.postcardBtn.addEventListener('click', () => this.cb?.onPostcard?.());
  }

  open(state: LetterModalState, cb: LetterCallbacks): void {
    this.currentPersona = state.persona;
    this.cb = cb;
    this.titleEl.textContent = `${state.persona.emoji} ${state.persona.shortName}`;
    this.subEl.textContent = `${state.persona.species} · ${state.persona.voice}`;
    this.replyEl.textContent = '';
    this.replyEl.classList.remove('show');
    this.textareaEl.value = '';
    this.textareaEl.disabled = false;
    this.sendBtn.disabled = false;
    this.sendBtn.textContent = '发送';
    this.postcardBtn.disabled = true;
    this.toastEl.textContent = '';
    this.toastEl.classList.remove('show');
    this.busy = false;
    this.root.classList.remove('hidden');
    requestAnimationFrame(() => {
      this.root.classList.add('show');
      this.textareaEl.focus({ preventScroll: true });
    });
  }

  appendReplyChunk(chunk: string): void {
    if (!this.replyEl.classList.contains('show')) this.replyEl.classList.add('show');
    this.replyEl.textContent = (this.replyEl.textContent ?? '') + chunk;
    this.replyEl.scrollTop = this.replyEl.scrollHeight;
  }

  setReply(text: string): void {
    this.replyEl.classList.add('show');
    this.replyEl.textContent = text;
  }

  getReplyText(): string {
    return this.replyEl.textContent ?? '';
  }

  enablePostcard(): void {
    this.postcardBtn.disabled = false;
  }

  showToast(msg: string): void {
    this.toastEl.textContent = msg;
    this.toastEl.classList.add('show');
  }

  flashReplyEdge(): void {
    this.replyEl.classList.remove('flash');
    void this.replyEl.offsetWidth;
    this.replyEl.classList.add('flash');
    setTimeout(() => this.replyEl.classList.remove('flash'), 700);
  }

  setBusy(b: boolean, label?: string): void {
    this.busy = b;
    this.sendBtn.disabled = b;
    this.textareaEl.disabled = b;
    this.sendBtn.textContent = label ?? (b ? '发送中...' : '发送');
  }

  private async handleSend(): Promise<void> {
    if (this.busy) return;
    const text = this.textareaEl.value.trim();
    if (!text) {
      this.showToast('写一句再发吧 ✏️');
      return;
    }
    if (!this.cb) return;
    this.setBusy(true);
    try {
      await this.cb.onSend(text);
      this.setBusy(false, '继续追问');
      this.enablePostcard();
    } catch (err) {
      this.setBusy(false, '重试');
      this.showToast('网络小风暴，再试一次？');
      showToast('AI 暂时离线，已为你准备一段回信', 'info', 2800);
      console.warn('[letter] send failed', err);
    }
  }

  private handleClose(): void {
    this.root.classList.remove('show');
    setTimeout(() => {
      this.root.classList.add('hidden');
      this.cb?.onClose();
      this.cb = null;
    }, 220);
  }

  isOpen(): boolean {
    return !this.root.classList.contains('hidden');
  }

  getCurrentPersona(): Persona | null {
    return this.currentPersona;
  }
}
